import { API_BASE_URL } from '../config';

export async function askLandscapingQuestion(question, options = {}) {
  const { asker_email, notify_expert_note } = options;
  const body = { question };
  if (typeof asker_email === 'string' && asker_email.trim()) {
    body.asker_email = asker_email.trim().toLowerCase();
  }
  if (notify_expert_note === true) {
    body.notify_expert_note = true;
  }
  const res = await fetch(`${API_BASE_URL}/public/landscaping-questions/ask`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || data.details || 'Request failed');
  return data;
}

/**
 * Combined get-ideas endpoint: text question required, photo and location optional.
 * Returns {id, question_text, ai_answer, analysis, matchedDesigners, matchedPlants, expert_notes}.
 */
export async function getIdeas({
  question,
  image_base64,
  user_location,
  asker_email,
  notify_expert_note,
  is_shared,
}) {
  const body = {
    question,
    image_base64: image_base64 || null,
    user_location: user_location || null,
  };
  if (typeof asker_email === 'string' && asker_email.trim()) {
    body.asker_email = asker_email.trim().toLowerCase();
  }
  if (notify_expert_note === true) {
    body.notify_expert_note = true;
  }
  if (is_shared === true) {
    body.is_shared = true;
  }
  const res = await fetch(`${API_BASE_URL}/yard/get-ideas`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || data.details || 'Request failed');
  return data;
}

export async function getPublicLandscapingQuestion(id) {
  const res = await fetch(`${API_BASE_URL}/public/landscaping-questions/${encodeURIComponent(id)}`);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Not found');
  return data;
}

/** Opt-in Community feed: searchable Get Ideas posts (no PII). */
export async function getCommunityQuestions({ q, city, state, limit = 30, offset = 0 } = {}) {
  const sp = new URLSearchParams();
  if (typeof q === 'string' && q.trim()) sp.set('q', q.trim());
  if (typeof city === 'string' && city.trim()) sp.set('city', city.trim());
  if (typeof state === 'string' && state.trim()) sp.set('state', state.trim());
  sp.set('limit', String(limit));
  sp.set('offset', String(offset));
  const res = await fetch(`${API_BASE_URL}/community/questions?${sp.toString()}`);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || data.details || 'Failed to load');
  return data.items || [];
}

/** Recent answered questions for Dear John column (published expert notes only). */
export async function listPublicLandscapingQuestions(limit = 30) {
  const res = await fetch(
    `${API_BASE_URL}/public/landscaping-questions?limit=${encodeURIComponent(limit)}`
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || data.details || 'Failed to load');
  return data.items || [];
}

function getAuthHeaders() {
  const token = localStorage.getItem('authToken') || localStorage.getItem('adminToken');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

/** Current designer profile (business name, etc.) for prefilling expert note labels. */
export async function getDesignerProfileForSession() {
  const token = localStorage.getItem('authToken') || localStorage.getItem('adminToken');
  if (!token) return null;
  const res = await fetch(`${API_BASE_URL}/designers/profile`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data.profile) return null;
  return data.profile;
}

/** Display label: business name, then display name, then full name. */
export function designerProfileNoteLabel(profile) {
  if (!profile) return '';
  return (
    (profile.businessName && String(profile.businessName).trim()) ||
    (profile.displayName && String(profile.displayName).trim()) ||
    (profile.fullName && String(profile.fullName).trim()) ||
    ''
  );
}

/** Designer or admin: add expert note (default pending until admin publishes). */
export async function postDesignerCommunityNote(questionId, body) {
  const res = await fetch(
    `${API_BASE_URL}/designers/community-questions/${encodeURIComponent(questionId)}/notes`,
    {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(body),
    }
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || data.details || 'Failed to post note');
  return data.note;
}

function getAdminHeaders() {
  const token = localStorage.getItem('adminToken');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

export async function adminListLandscapingQuestions(limit = 50) {
  const res = await fetch(`${API_BASE_URL}/admin/landscaping-questions?limit=${limit}`, {
    headers: getAdminHeaders(),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Failed to load');
  return data.items || [];
}

export async function adminAddExpertNote(questionId, body) {
  const res = await fetch(`${API_BASE_URL}/admin/landscaping-questions/${encodeURIComponent(questionId)}/expert-notes`, {
    method: 'POST',
    headers: getAdminHeaders(),
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || data.details || 'Failed');
  return data.note;
}

export async function adminPatchExpertNote(questionId, noteId, body) {
  const res = await fetch(
    `${API_BASE_URL}/admin/landscaping-questions/${encodeURIComponent(questionId)}/expert-notes/${encodeURIComponent(noteId)}`,
    {
      method: 'PATCH',
      headers: getAdminHeaders(),
      body: JSON.stringify(body),
    }
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Failed');
  return data.note;
}

export async function adminDeleteExpertNote(questionId, noteId) {
  const res = await fetch(
    `${API_BASE_URL}/admin/landscaping-questions/${encodeURIComponent(questionId)}/expert-notes/${encodeURIComponent(noteId)}`,
    {
      method: 'DELETE',
      headers: getAdminHeaders(),
    }
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Failed');
  return data;
}

export async function adminPatchQuestion(questionId, body) {
  const res = await fetch(
    `${API_BASE_URL}/admin/landscaping-questions/${encodeURIComponent(questionId)}`,
    {
      method: 'PATCH',
      headers: getAdminHeaders(),
      body: JSON.stringify(body),
    }
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || data.details || 'Failed');
  return data.question;
}

export async function adminDeleteQuestion(questionId) {
  const res = await fetch(
    `${API_BASE_URL}/admin/landscaping-questions/${encodeURIComponent(questionId)}`,
    {
      method: 'DELETE',
      headers: getAdminHeaders(),
    }
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Failed');
  return data;
}
